import { createFileRoute, redirect } from '@tanstack/react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { IntegrationRow } from '@/components/features/integration/Integration-row';
import { RequestIntegrationCard } from '@/components/features/integration/request-integration-card';
import { Can } from '@/components/features/permission/can';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { integrations } from '@/config/integrations';
import { capitalize } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute(
  '/_authenticated/organization/integration'
)({
  component: RouteComponent,
  validateSearch: z.object({
    integration: z.string().optional(),
    status: z.enum(['error', 'success']).optional(),
  }),
  beforeLoad: ({ search, context }) => {
    const { t } = context;

    if (search.integration) {
      if (search.status === 'error') {
        toast.error(
          t('authenticated.organization.integration.toast.connectionFailed', {
            integration: capitalize(search.integration),
          })
        );
      }

      if (search.status === 'success') {
        toast.success(
          t('authenticated.organization.integration.toast.connected', {
            integration: capitalize(search.integration),
          })
        );
      }

      throw redirect({
        // eslint-disable-next-line
        to: location.pathname,
        search: {},
        replace: true,
      });
    }
  },
});

function RouteComponent() {
  const { t } = useTranslation();

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <PageHeaderTitle>
            {t('authenticated.organization.integration.title')}
          </PageHeaderTitle>
          <PageHeaderDescription>
            {t('authenticated.organization.integration.description')}
          </PageHeaderDescription>
        </PageHeaderInfo>
      </PageHeader>

      <PageContent>
        <div className="space-y-6">
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">
                    {t(
                      'authenticated.organization.integration.table.integration'
                    )}
                  </TableHead>
                  <TableHead>
                    {t(
                      'authenticated.organization.integration.table.description'
                    )}
                  </TableHead>
                  <Can
                    permissions={[
                      'integration:install',
                      'integration:uninstall',
                    ]}
                  >
                    <TableHead className="w-[180px] text-right">
                      {t(
                        'authenticated.organization.integration.table.actions'
                      )}
                    </TableHead>
                  </Can>
                </TableRow>
              </TableHeader>
              <TableBody>
                {integrations.map((integration) => (
                  <IntegrationRow
                    key={integration.provider}
                    integration={integration}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          <RequestIntegrationCard />
        </div>
      </PageContent>
    </Page>
  );
}
